import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

// Search engines and answer engines truncate descriptions around 160 chars.
// Clamp on a word boundary so we never ship a dangling half word.
const clamp = (text: string, max = 160): string => {
    const clean = text.replace(/\s+/g, ' ').trim()

    if (clean.length <= max) return clean

    const cut = clean.slice(0, max - 1)

    return `${cut.slice(0, cut.lastIndexOf(' ')).trim()}…`
}

const absolute = (path: string): string =>
    path.startsWith('http') ? path : `${siteConfig.url}${path.startsWith('/') ? '' : '/'}${path}`

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    ogImageAlt,
    ogType = 'website',
    canonical,
    markdownPath,
    noindex = false,
    publishedTime,
    modifiedTime,
    author,
    section,
    tags
}): ReactNode => {
    const pageTitle = title
        ? `${title} | ${siteConfig.name}`
        : `${siteConfig.name} | Evidence-informed health knowledge`

    const metaDescription = clamp(description)
    const url = canonical ? absolute(canonical) : siteConfig.url

    const usingDefaultImage = !ogImage
    const imageUrl = absolute(ogImage ?? siteConfig.ogImage)
    const imageAlt = ogImageAlt ?? (title ?? siteConfig.name)

    const robots = noindex
        ? 'noindex, follow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

    return (
        <Head>
            <title>{pageTitle}</title>

            <meta name='description' content={metaDescription} />
            <meta name='robots' content={robots} />
            <meta name='googlebot' content={robots} />
            <link rel='canonical' href={url} />
            {markdownPath && (
                <link rel='alternate' type='text/markdown' href={absolute(markdownPath)} />
            )}

            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={metaDescription} />
            <meta property='og:type' content={ogType} />
            <meta property='og:url' content={url} />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:locale' content={siteConfig.locale} />

            <meta property='og:image' content={imageUrl} />
            <meta property='og:image:alt' content={imageAlt} />
            {usingDefaultImage && (
                <>
                    <meta property='og:image:width' content={String(siteConfig.ogImageWidth)} />
                    <meta property='og:image:height' content={String(siteConfig.ogImageHeight)} />
                </>
            )}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={metaDescription} />
            <meta name='twitter:image' content={imageUrl} />
            <meta name='twitter:image:alt' content={imageAlt} />

            {ogType === 'article' && publishedTime && (
                <meta property='article:published_time' content={publishedTime} />
            )}
            {ogType === 'article' && modifiedTime && (
                <meta property='article:modified_time' content={modifiedTime} />
            )}
            {ogType === 'article' && author && <meta property='article:author' content={author} />}
            {ogType === 'article' && section && <meta property='article:section' content={section} />}
            {ogType === 'article' &&
                tags?.map(tag => <meta key={tag} property='article:tag' content={tag} />)}
        </Head>
    )
}
